/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { requestFullLocationAccess } from "@src/appV2/Location/utils/permissions";
import { PROFILE_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { mapFormDataToPreferenceDetails } from "@src/appV2/redesign/PlacementCandidate/utils";
import {
  INTERVIEW_TYPE,
  interviewsApiUrl,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type QueryObserverResult, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { useCredentialRequirementConfiguration } from "../../../Documents/useCredentialRequirementConfiguration";
import { useCreateChildcareSupportChat } from "../../api/useCreateChildcareSupportChat";
import { PLACEMENT_TYPE } from "../../api/useCreatePlacementApplication";
import {
  type GetAvailabilityResponse,
  type InterviewAvailability,
} from "../../api/useGetInterviewAvailability";
import { type PlacementDetail, transformPlacementData } from "../../api/useGetPlacement";
import { useBookingOrderingCohort } from "../../hooks/useBookingOrderingCohort";
import { useRecruiterCallTimeStorage } from "../../hooks/useRecruiterCallTimeStorage";
import { PLACEMENT_STATUS_MAP, type PlacementResponse } from "../../types/fetchPlacements.schema";
import {
  filterAvailabilityForSubmitDocumentsLater,
  getInterviewAvailability,
} from "../../utils/reliabilityRule";
import { AlternativeAddressModal } from "../AlternativeAddressModal";
import { BookFirstInterviewConfirmationDialog } from "../BookFirstInterviewConfirmationDialog";
import { ChildcareSupportChatBottomSheet } from "../ChildcareSupportChat/ChildcareSupportChatBottomSheet";
import { EasySchedulingInterviewRequestModal } from "../EasySchedulingInterviewRequestModal/EasySchedulingInterviewRequestModal";
import { ExternalApplicationConfirmationDialog } from "../ExternalApplicationConfirmationDialog";
import { ExternalApplicationDialog } from "../ExternalApplicationDialog";
import { InterviewBookedDialog } from "../InterviewBookedDialog";
import { InterviewCancelBottomSheet } from "../InterviewCancelBottomSheet";
import { InterviewCancelButton } from "../InterviewCancelButton";
import { InterviewLocationPermissionBottomSheet } from "../InterviewLocationPermissionBottomSheet";
import { InterviewReliabilityBottomSheet } from "../InterviewReliabilityBottomSheet";
import { InterviewTodayRescheduleBottomSheet } from "../InterviewTodayRescheduleBottomSheet";
import { InterviewUpdateEtaContainer } from "../InterviewUpdateEta/InterviewUpdateEtaContainer";
import { INTERVIEW_UPDATE_ETA_SOURCE } from "../InterviewUpdateEta/types";
import { JobSearchAgreementConfirmationModal } from "../JobSearchAgreementConfirmationModal";
import { JobSearchAgreementModal } from "../JobSearchAgreementModal";
import { LocationPermissionDialog } from "../LocationPermissionDialog";
import { PastInterviewCancelBottomSheet } from "../PastInterviewCancelBottomSheet";
import { PhoneConfirmationBottomSheet } from "../PhoneConfirmationBottomSheet/PhoneConfirmationBottomSheet";
import { PlacementApplicationPreferenceFormDialog } from "../PlacementApplicationPreferenceForm/PlacementApplicationPreferenceFormDialog";
import { PlacementApplicationSuccessBottomSheet } from "../PlacementApplicationSuccessBottomSheet";
import { PlacementDetailBookInterviewDialog } from "../PlacementDetailBookInterviewDialog";
import { PlacementDetailCreateApplicationDialog } from "../PlacementDetailCreateApplicationDialog";
import { type AlternativeAddressData } from "../PlacementDetailsPage/hooks/useAlternativeAddress";
import { RecruiterScreeningCallDialog } from "../RecruiterScreeningCall/RecruiterScreeningCallDialog";
import { SubmitDocumentsLaterInterviewBookedBottomSheet } from "../SubmitDocumentsLaterInterviewBookedBottomSheet";
import { SubmitInterviewDocumentsLaterBottomSheet } from "../SubmitInterviewDocumentsLaterBottomSheet";
import { VerifyInterviewAttendanceBottomSheet } from "../VerifyInterviewAttendance/VerifyInterviewAttendanceBottomSheet";
import { WalkInNoteDialog } from "../WalkInNoteDialog";
import { usePlacementActions } from "./hooks/usePlacementActions";
import { PlacementDetailsReferralButton } from "./PlacementDetailsReferralButton";
import { PlacementPrimaryAction } from "./PlacementPrimaryAction";
import { PLACEMENT_ACTION_TYPE } from "./utils/getPrimaryAction";

interface PlacementApplyButtonContainerProps {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  interviewAvailability: InterviewAvailability;
  isLoadingInterviewAvailability: boolean;
  showAlternativeAddressInformation: boolean;
  alternativeAddressData?: AlternativeAddressData;
  isEasySchedulingDeepLink?: boolean;
  isEasySchedulingPlacement?: boolean;
  refetchPlacement: () => Promise<QueryObserverResult<PlacementResponse>>;
  refetchInterviewAvailability: () => Promise<QueryObserverResult<GetAvailabilityResponse>>;
  refetchPlacementCandidate: () => Promise<QueryObserverResult<PlacementCandidate>>;
}

// eslint-disable-next-line complexity
export function PlacementApplyButtonContainer(props: PlacementApplyButtonContainerProps) {
  const {
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    showAlternativeAddressInformation,
    alternativeAddressData,
    isEasySchedulingDeepLink = false,
    isEasySchedulingPlacement = false,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  } = props;

  const { mutateAsync: createChildcareSupportChat, isLoading: isCreatingChildcareSupportChat } =
    useCreateChildcareSupportChat();
  const { showErrorToast, showWarningToast } = useToast();
  const bookingReorderingCohort = useBookingOrderingCohort();
  const queryClient = useQueryClient();
  const { saveRecruiterCallTime, clearRecruiterCallTime } = useRecruiterCallTimeStorage();

  // hook call that provides all state and operations
  const actions = usePlacementActions({
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    alternativeAddressData,
    showAlternativeAddressInformation,
    isEasySchedulingPlacement,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  });

  // Easy scheduling deep link - auto-open booking dialog
  const history = useHistory();
  const worker = useDefinedWorker();

  useLogEffect(
    APP_V2_APP_EVENTS.EASY_SCHEDULING_DEEP_LINK_TRIGGERED,
    {
      placementId: placementDetails.id,
      schedulingType: "easy-scheduling",
    },
    { enabled: isEasySchedulingDeepLink }
  );

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_CONNECTED_LISTING_VIEWED,
    {
      placementId: placementDetails.id,
      connectedPlacementId: actions.derivedData.connectedPlacementId,
      isConnectedListingInOtherWorkplace:
        actions.derivedData.isConnectedListingInterviewInOtherWorkplace,
    },
    {
      enabled: actions.primaryAction.type === PLACEMENT_ACTION_TYPE.HAS_CONNECTED_LISTING_INTERVIEW,
    }
  );

  useEffect(() => {
    if (
      isEasySchedulingDeepLink &&
      !actions.modals.bookInterview.modalIsOpen &&
      !isLoadingInterviewAvailability &&
      actions.status.canScheduleInterviewFromDeepLink &&
      actions.status.hasAcceptedApplication &&
      isDefined(actions.status.actionType)
    ) {
      const searchParameters = new URLSearchParams(history.location.search);
      searchParameters.delete("scheduling-type");
      searchParameters.delete("requestEasySchedulingInterview");
      history.replace({
        search: searchParameters.toString(),
      });

      void actions.interview.openBookInterviewModal(
        actions.status.actionType,
        interviewAvailability,
        false
      );
    }
  }, [
    isEasySchedulingDeepLink,
    actions.modals.bookInterview.modalIsOpen,
    actions.interview,
    interviewAvailability,
    isLoadingInterviewAvailability,
    history,
    actions.status.canScheduleInterviewFromDeepLink,
    actions.status.hasAcceptedApplication,
    actions.status.actionType,
  ]);

  // Update ETA deep link - auto-open modal when query param is present
  useEffect(() => {
    const searchParameters = new URLSearchParams(history.location.search);
    const updateEtaParameter = searchParameters.get("updateEta");

    if (
      updateEtaParameter === "true" &&
      actions.derivedData.hasInProgressInterview &&
      !actions.modals.updateInterviewEta.modalIsOpen
    ) {
      searchParameters.delete("updateEta");
      history.replace({
        search: searchParameters.toString(),
      });

      actions.modals.updateInterviewEta.openModal();
    }
  }, [history, actions.derivedData.hasInProgressInterview, actions.modals.updateInterviewEta]);

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  return (
    <>
      <Box
        sx={(theme) => ({
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
          paddingTop: theme.spacing(6),
          paddingBottom: theme.spacing(6),
          backgroundColor: theme.palette.background.tertiary,
          borderTop: 1,
          borderColor: theme.palette.divider,
          zIndex: theme.zIndex.drawer,
        })}
      >
        {isDefined(placementDetails) && isDefined(placementCandidate) && (
          <Stack spacing={5}>
            {isDefined(actions.primaryActionProps.type) && (
              <PlacementPrimaryAction
                type={actions.primaryActionProps.type}
                label={actions.primaryActionProps.label}
                warning={actions.primaryActionProps.warning}
                description={actions.primaryActionProps.description}
                disabled={
                  actions.primaryActionProps.disabled ||
                  (placementDetails.status !== PLACEMENT_STATUS_MAP.OPEN &&
                    placementDetails.status !== PLACEMENT_STATUS_MAP.WAITLIST)
                }
                isLoading={actions.primaryActionProps.isLoading}
                startIcon={actions.primaryActionProps.startIcon}
                additionalContent={actions.primaryActionProps.additionalContent}
                onClick={actions.primaryActionProps.onClick}
              />
            )}

            {actions.primaryAction.showCancelInterviewButton && (
              <InterviewCancelButton
                isProspectWorkplace={Boolean(
                  placementDetails.workplace.placementSettings?.isProspect
                )}
                disabled={false}
                interviewType={actions.interview.interviewData?.attributes.type}
                onClick={() => {
                  actions.modals.cancelInterview.openModal();
                }}
              />
            )}
            {actions.primaryAction.showReferralButton && (
              <PlacementDetailsReferralButton
                eventContext={actions.derivedData.eventContext}
                highestRankingSharedQualification={
                  actions.derivedData.highestRankingSharedQualification
                }
                placementDetails={placementDetails}
              />
            )}
          </Stack>
        )}
      </Box>

      {actions.modals.placementApplicationSuccess.modalIsOpen && (
        <PlacementApplicationSuccessBottomSheet
          modalState={actions.modals.placementApplicationSuccess}
          placementDetails={placementDetails}
        />
      )}

      {actions.modals.walkInNote.modalIsOpen && (
        <WalkInNoteDialog
          modalState={actions.modals.walkInNote}
          workerType={placementDetails.workerTypes?.join(", ")}
          onContinue={async () => {
            actions.modals.walkInNote.closeModal();
            // Continue the book interview flow after prospect check
            await actions.flows.continueBookInterview();
          }}
        />
      )}

      {actions.modals.showAlternativeAddress.modalIsOpen && isDefined(alternativeAddressData) && (
        <AlternativeAddressModal
          modalState={actions.modals.showAlternativeAddress}
          placementDetails={placementDetails}
          alternativeAddressData={alternativeAddressData}
          onContinue={async () => {
            actions.modals.showAlternativeAddress.closeModal();
            // Continue the book interview flow after alternative address check
            await actions.flows.continueBookInterview();
          }}
        />
      )}

      {actions.modals.bookInterview.modalIsOpen && (
        <PlacementDetailBookInterviewDialog
          placementDetails={placementDetails}
          isReschedule={
            actions.interview.bookInterviewModalType === PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW
          }
          modalState={actions.modals.bookInterview}
          interviewAvailability={(() => {
            const baseAvailability = getInterviewAvailability({
              interviewAvailability,
              workerTimezone: actions.derivedData.workerTimezone,
              workplaceTimezone: placementDetails.workplace.tmz,
              isRescheduleForToday: actions.interview.isRescheduleForToday,
            });
            // When booking from submit documents later flow, filter out same-day slots
            // and next-day slots if it's after 2 PM (to allow time for document upload)
            if (actions.interview.isBookingFromSubmitDocumentsLater) {
              return filterAvailabilityForSubmitDocumentsLater({
                interviewAvailability: baseAvailability,
                workerTimezone: actions.derivedData.workerTimezone,
              });
            }
            return baseAvailability;
          })()}
          interviewType={actions.interview.interviewData?.attributes.type}
          placementId={placementDetails.id}
          bookingReorderingCohort={bookingReorderingCohort}
          onBookInterview={async (time) => {
            actions.interview.setSelectedInterviewTime(time);
            actions.modals.bookInterview.closeModal();

            saveRecruiterCallTime(placementDetails.id, time);

            // For screen_last cohort, show screening modal after time selection
            // Eligibility check is in derivedData (includes cohort, screening status, showCallMeNow, isRecruiterScreeningEnabled)
            if (
              actions.derivedData.isEligibleForScreenLastScreeningModal &&
              actions.interview.bookInterviewModalType === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW
            ) {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_RECRUITER_SCREENING_CALL_MODAL_OPENED, {
                ...actions.derivedData.eventContext,
                placementCandidateId: placementCandidate.id,
                showCallMeNow: actions.derivedData.showCallMeNow,
                source: "detail-cta",
              });
              actions.modals.screenLastStepScreening.openModal();
              return;
            }

            // Document check for childcare support chat flow (when submit documents later flag is NOT enabled)
            // Note: When isSubmitHiringDocumentsLaterEnabled is true, the check happens earlier in bookInterview flow
            if (
              actions.derivedData.hasMissingOrPendingDocuments &&
              !actions.derivedData.isSubmitHiringDocumentsLaterEnabled
            ) {
              // Use childcare support chat flow
              try {
                await createChildcareSupportChat({
                  workerId: worker.userId,
                  placementId: placementDetails.id,
                });

                actions.childcareChatState.setChildcareChatContinuationAction("bookInterview");
                actions.modals.childcareSupportChat.openModal();
                return;
              } catch {
                showErrorToast(
                  "Unable to set interview time. Please try again or contact support."
                );
                return;
              }
            }

            if (
              !actions.interview.isBookFirstCohort &&
              actions.interview.bookInterviewModalType !==
                PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW &&
              placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType !==
                INTERVIEW_TYPE.PHONE_SCREEN
            ) {
              actions.modals.interviewReliability.openModal();
              return;
            }

            const success = await actions.interview.handleBookOrRescheduleInterview(time, {
              ...(actions.interview.isBookingFromSubmitDocumentsLater && {
                status: InterviewStatus.PENDING,
              }),
            });
            // Clear stored interview time after successful booking
            if (success) {
              clearRecruiterCallTime(placementDetails.id);
            }
          }}
        />
      )}

      {actions.modals.interviewBooked.modalIsOpen &&
        isDefined(actions.interview.selectedInterviewTime?.start) &&
        (actions.derivedData.isEligibleForBookFirstConfirmation ? (
          <BookFirstInterviewConfirmationDialog
            modalState={actions.modals.interviewBooked}
            placementId={placementDetails.id}
            interviewDate={new Date(actions.interview.selectedInterviewTime.start)}
            source="detail-cta"
            bookingReorderingCohort={bookingReorderingCohort}
          />
        ) : (
          <InterviewBookedDialog
            modalState={actions.modals.interviewBooked}
            placementDetails={placementDetails}
            selectedInterviewTime={actions.interview.selectedInterviewTime}
            interviewType={actions.interview.interviewData?.attributes.type}
            workplaceStartHour={actions.interview.workplaceStartHour}
            isRecruiterScreeningEnabled={actions.derivedData.isRecruiterScreeningEnabled}
          />
        ))}

      {actions.modals.interviewReliability.modalIsOpen &&
        isDefined(actions.interview.selectedInterviewTime) &&
        isDefined(actions.interview.workplaceStartHour) && (
          <InterviewReliabilityBottomSheet
            modalState={actions.modals.interviewReliability}
            placementDetails={placementDetails}
            placementCandidate={placementCandidate}
            workplaceStartHour={actions.interview.workplaceStartHour}
            onContinue={async () => {
              actions.modals.interviewReliability.closeModal();
              if (actions.interview.selectedInterviewTime) {
                await actions.interview.handleBookOrRescheduleInterview(
                  actions.interview.selectedInterviewTime,
                  {
                    ...(actions.interview.isBookingFromSubmitDocumentsLater && {
                      status: InterviewStatus.PENDING,
                    }),
                  }
                );
              }
            }}
          />
        )}

      {actions.modals.childcareSupportChat.modalIsOpen && (
        <ChildcareSupportChatBottomSheet
          modalState={actions.modals.childcareSupportChat}
          placementDetails={placementDetails}
          placementCandidate={placementCandidate}
          isLoading={isCreatingChildcareSupportChat}
          onClose={async () => {
            actions.modals.childcareSupportChat.closeModal();

            const placementResult = await refetchPlacement();
            if (!isDefined(placementResult.data)) {
              showWarningToast("Unable to fetch placement details. Please try again.");
              return;
            }

            const updatedPlacementDetails = transformPlacementData(
              placementResult.data,
              credentialRequirementConfiguration
            );
            const hasMissingOrPendingDocuments = updatedPlacementDetails?.workerRequirements?.some(
              (requirement) => !requirement.attributes.isValid
            );

            if (hasMissingOrPendingDocuments) {
              showWarningToast("Please complete your documents to continue");
              return;
            }

            const { childcareChatContinuationAction } = actions.childcareChatState;
            actions.childcareChatState.setChildcareChatContinuationAction(undefined);

            if (childcareChatContinuationAction === "apply") {
              await actions.flows.applyToJob();
            } else if (childcareChatContinuationAction === "easyScheduling") {
              await actions.flows.easySchedulingInterviewRequest();
            } else if (
              placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType !==
              INTERVIEW_TYPE.PHONE_SCREEN
            ) {
              actions.modals.interviewReliability.openModal();
            }
          }}
        />
      )}

      {actions.modals.cancelInterview.modalIsOpen &&
        isDefined(actions.interview.interviewData?.id) && (
          <InterviewCancelBottomSheet
            isProspectWorkplace={Boolean(placementDetails.workplace.placementSettings?.isProspect)}
            modalState={actions.modals.cancelInterview}
            interviewId={actions.interview.interviewData.id}
            isLateCancellation={actions.interview.isLateCancellation}
            workplaceStartHour={actions.interview.workplaceStartHour}
            isSchedulingPastWorkplaceStartHour={
              actions.derivedData.isSchedulingTodayAfterWorkplaceStartHour
            }
            attendanceScore={placementCandidate.attendanceScore}
            interviewType={actions.interview.interviewData.attributes.type}
            onCancelInterview={async () => {
              await Promise.all([
                refetchInterviewAvailability(),
                refetchPlacement(),
                refetchPlacementCandidate(),
                queryClient.invalidateQueries({ queryKey: [interviewsApiUrl] }),
              ]);
            }}
          />
        )}

      {actions.modals.cancelPastInterview.modalIsOpen && actions.interview.interviewData && (
        <PastInterviewCancelBottomSheet
          isProspectWorkplace={Boolean(placementDetails.workplace.placementSettings?.isProspect)}
          modalState={actions.modals.cancelPastInterview}
          interviewId={actions.interview.interviewData.id}
          startsInWorkplaceToday={actions.derivedData.startsInWorkplaceToday}
          attendanceScore={placementCandidate.attendanceScore}
          interviewType={actions.interview.interviewData.attributes.type}
          onCancelInterview={async () => {
            await Promise.all([
              refetchInterviewAvailability(),
              refetchPlacement(),
              refetchPlacementCandidate(),
              queryClient.invalidateQueries({ queryKey: [interviewsApiUrl] }),
            ]);
          }}
        />
      )}

      {actions.modals.lateRescheduleToday.modalIsOpen &&
        isDefined(actions.interview.interviewData) && (
          <InterviewTodayRescheduleBottomSheet
            modalState={actions.modals.lateRescheduleToday}
            interviewId={actions.interview.interviewData.id}
            attendanceScore={placementCandidate.attendanceScore}
            interviewType={actions.interview.interviewData.attributes.type}
            onCancel={() => {
              actions.modals.lateRescheduleToday.closeModal();
            }}
            onReschedule={async () => {
              actions.modals.lateRescheduleToday.closeModal();
              await actions.interview.openBookInterviewModal(
                PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW,
                interviewAvailability,
                true
              );
            }}
          />
        )}

      {actions.modals.createApplication.modalIsOpen && (
        <PlacementDetailCreateApplicationDialog
          modalState={actions.modals.createApplication}
          workplaceName={placementDetails.workplace.name}
          placementCandidateId={placementCandidate.id}
          allowRequestInterview={placementDetails.allowRequestInterview ?? false}
          onApply={async (workerInterviewRequest, workerVoiceNote) => {
            actions.modals.createApplication.closeModal();
            await actions.application.handleApply({
              workerInterviewRequest,
              workerVoiceNote,
              placementType: PLACEMENT_TYPE.verified,
              preferences: actions.application.applicationPreferences,
            });
          }}
        />
      )}

      {actions.modals.locationPermission.modalIsOpen && (
        <LocationPermissionDialog
          modalState={actions.modals.locationPermission}
          isOsWithoutAlwaysAllow={actions.interview.isOsWithoutAlwaysAllow}
          onConfirm={() => {
            actions.modals.locationPermission.closeModal();
            void requestFullLocationAccess();
          }}
        />
      )}

      {actions.modals.externalApplication.modalIsOpen && (
        <ExternalApplicationDialog
          modalState={actions.modals.externalApplication}
          placement={placementDetails}
          placementCandidate={placementCandidate}
          isApplying={actions.isLoading}
          onApply={async () => {
            await actions.application.handleApply({
              placementType: PLACEMENT_TYPE.unverified,
              preferences: actions.application.applicationPreferences,
              applicationManagementType: actions.application.applicationManagementType,
            });
            actions.modals.externalApplication.closeModal();
            actions.modals.externalApplicationConfirmation.openModal();
            actions.application.setApplicationManagementType(undefined);
          }}
          onClose={() => {
            actions.application.setApplicationManagementType(undefined);
          }}
        />
      )}

      {actions.modals.externalApplicationConfirmation.modalIsOpen && (
        <ExternalApplicationConfirmationDialog
          modalState={actions.modals.externalApplicationConfirmation}
          placement={placementDetails}
          placementCandidate={placementCandidate}
        />
      )}

      {actions.modals.interviewLocationPermission.modalIsOpen && (
        <InterviewLocationPermissionBottomSheet
          modalState={actions.modals.interviewLocationPermission}
          isOsWithoutAlwaysAllow={actions.interview.isOsWithoutAlwaysAllow}
          onConfirm={async () => {
            await actions.interview.openBookInterviewModal(
              actions.interview.bookInterviewModalType ?? PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
              interviewAvailability,
              actions.interview.isRescheduleForToday
            );
          }}
        />
      )}

      {actions.recruiterScreeningCallFlowState.modalState.modalIsOpen && (
        <RecruiterScreeningCallDialog
          modalState={actions.recruiterScreeningCallFlowState.modalState}
          placementId={placementDetails.id}
          source="detail-cta"
          placementCandidate={placementCandidate}
          bookingReorderingCohort={bookingReorderingCohort}
        />
      )}

      {actions.modals.screenLastStepScreening.modalIsOpen &&
        isDefined(actions.interview.selectedInterviewTime) && (
          <RecruiterScreeningCallDialog
            modalState={actions.modals.screenLastStepScreening}
            placementId={placementDetails.id}
            source="detail-cta"
            placementCandidate={placementCandidate}
            bookingReorderingCohort={bookingReorderingCohort}
            selectedInterviewTime={actions.interview.selectedInterviewTime}
          />
        )}

      {actions.modals.applicationPreference.modalIsOpen &&
        (placementDetails.allowApplicationPreferenceCollection ?? false) && (
          <PlacementApplicationPreferenceFormDialog
            modalState={actions.modals.applicationPreference}
            placementCandidateId={placementCandidate.id}
            applicationQualification={actions.derivedData.highestRankingSharedQualification}
            placementDetails={placementDetails}
            onSavePreferences={(data) => {
              const mapped = mapFormDataToPreferenceDetails(data);
              actions.application.setApplicationPreferences(mapped);
              actions.modals.applicationPreference.closeModal();
              // Execute the original requested action with the preferences data
              void actions.application.pendingActionRef.current?.(mapped);
            }}
          />
        )}

      {actions.modals.easySchedulingInterviewRequest.modalIsOpen && (
        <EasySchedulingInterviewRequestModal
          modalState={actions.modals.easySchedulingInterviewRequest}
          placementDetails={placementDetails}
          isSubmitting={actions.isLoading}
        />
      )}

      {actions.modals.phoneConfirmation.modalIsOpen && (
        <PhoneConfirmationBottomSheet
          modalState={actions.modals.phoneConfirmation}
          phoneNumber={worker.phone ?? "Phone number not available"}
          placementDetails={placementDetails}
          onConfirm={() => {
            actions.modals.phoneConfirmation.closeModal();
            actions.interview.continueAfterPhoneConfirmation(interviewAvailability);
          }}
          onUpdatePhone={() => {
            actions.modals.phoneConfirmation.closeModal();
            history.push(PROFILE_FULL_PATH);
          }}
        />
      )}

      {actions.modals.submitInterviewDocumentsLater.modalIsOpen && (
        <SubmitInterviewDocumentsLaterBottomSheet
          modalState={actions.modals.submitInterviewDocumentsLater}
          placementDetails={placementDetails}
          onConfirm={async () => {
            actions.modals.submitInterviewDocumentsLater.closeModal();
            // Mark that we're booking from submit documents later flow
            // This will filter out same-day slots (and next-day if after 2 PM) to allow document upload time
            actions.interview.setIsBookingFromSubmitDocumentsLater(true);
            // Continue with the book interview flow after user confirms
            await actions.flows.continueBookInterview();
          }}
          onDismiss={() => {
            actions.modals.submitInterviewDocumentsLater.closeModal();
          }}
        />
      )}

      {actions.modals.submitDocumentsLaterInterviewBooked.modalIsOpen &&
        isDefined(actions.interview.selectedInterviewTime) && (
          <SubmitDocumentsLaterInterviewBookedBottomSheet
            modalState={actions.modals.submitDocumentsLaterInterviewBooked}
            interviewDate={actions.interview.selectedInterviewTime.start}
            onClose={() => {
              actions.modals.submitDocumentsLaterInterviewBooked.closeModal();
            }}
          />
        )}

      {actions.modals.verifyInterviewAttendance.modalIsOpen &&
        isDefined(actions.derivedData.unverifiedBookedInterview) && (
          <VerifyInterviewAttendanceBottomSheet
            modalState={actions.modals.verifyInterviewAttendance}
            interviewId={actions.derivedData.unverifiedBookedInterview.id}
            onSuccess={async () => {
              await Promise.all([refetchPlacement(), refetchPlacementCandidate()]);
            }}
          />
        )}

      {actions.modals.jobSearchAgreement.modalIsOpen && (
        <JobSearchAgreementModal
          modalState={actions.modals.jobSearchAgreement}
          placementId={actions.jobSearchAgreement.placementId}
          onComplete={actions.jobSearchAgreement.handleAgreementComplete}
        />
      )}

      {actions.modals.jobSearchAgreementConfirmation.modalIsOpen && (
        <JobSearchAgreementConfirmationModal
          modalState={actions.modals.jobSearchAgreementConfirmation}
          onContinueBooking={actions.jobSearchAgreement.handleAgreementContinueBooking}
        />
      )}

      {actions.modals.updateInterviewEta.modalIsOpen &&
        isDefined(actions.derivedData.inProgressInterview) && (
          <InterviewUpdateEtaContainer
            modalState={actions.modals.updateInterviewEta}
            interviewId={actions.derivedData.inProgressInterview.id}
            interviewStart={new Date(actions.derivedData.inProgressInterview.attributes.start)}
            workplaceId={placementDetails.workplace.id}
            workplaceTimezone={placementDetails.workplace.tmz}
            source={INTERVIEW_UPDATE_ETA_SOURCE.JOB_DETAILS}
            onEtaUpdated={async () => {
              await refetchPlacement();
            }}
          />
        )}
    </>
  );
}
/* eslint-enable max-lines */
